/**
 * three.js geometry for the far planes, built from the pure layout in ./layout.ts. Everything far is placed with
 * alongAzimuth() from the camera EYE, so the torii-aligned island really sits in the opening.
 */
import * as THREE from 'three';
import type { WorldState } from '../../world/state/types';
import { hash01 } from '../../world/random/hash';
import { ALIGNED_ISLAND_DISTANCE_M, EYE, SEA_LEVEL_Y, SHORE_M, alongAzimuth, groundY, outlineDistance } from '../composition';
import { TERRAIN_RADIUS_M } from '../ground/terrain';
import { SUGI_GROVE, TERRACE_BAND_M, insidePolygon, laneWeight, nearStairs, surfaceY } from '../site';
import type { Vec3 } from '../frame';
import { RIDGE_AZ_FROM, RIDGE_AZ_TO, RIDGE_LAYERS, TOWN, catenary, islandShape, islands, ridgeHeights, townLayout, windowCount, windowLit, type House } from './layout';

const AZ_FROM = RIDGE_AZ_FROM;
const AZ_TO = RIDGE_AZ_TO;

/** A camera-facing silhouette strip: top edge from heights (m above sea), bottom at sea level. */
function silhouette(azs: number[], dist: (i: number) => number, heights: number[], color: number): THREE.Mesh {
  const pos: number[] = [];
  const idx: number[] = [];
  azs.forEach((az, i) => {
    const d = dist(i);
    const top = alongAzimuth(az, d, SEA_LEVEL_Y + heights[i]!);
    const bottom = alongAzimuth(az, d, SEA_LEVEL_Y - 2);
    pos.push(...top, ...bottom);
    if (i > 0) {
      const a = (i - 1) * 2;
      idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return new THREE.Mesh(g, new THREE.MeshStandardMaterial({ color, roughness: 1, side: THREE.DoubleSide }));
}

export function buildRidges(seed: number): THREE.Group {
  const group = new THREE.Group();
  group.name = 'far_ridges';
  const samples = 360;
  const azs = Array.from({ length: samples }, (_, i) => AZ_FROM + ((AZ_TO - AZ_FROM) * i) / (samples - 1));
  const colors = [0x6f7f95, 0x55657a, 0x3f4c5c];
  RIDGE_LAYERS.forEach((layer, li) => {
    const mesh = silhouette(azs, () => layer.distanceM, ridgeHeights(seed, li, AZ_FROM, AZ_TO, samples), colors[li]!);
    mesh.name = `far_ridge_${li}`;
    mesh.renderOrder = -10 + li;
    group.add(mesh);
  });
  return group;
}

export function buildIslands(seed: number, toriiAzDeg: number): THREE.Group {
  const group = new THREE.Group();
  group.name = 'far_islands';
  for (const [n, isl] of islands(seed, toriiAzDeg).entries()) {
    const samples = 49; // odd: the middle vertex lands exactly on the island azimuth
    const azs = Array.from({ length: samples }, (_, i) => isl.azDeg - isl.halfWidthDeg + (2 * isl.halfWidthDeg * i) / (samples - 1));
    const heights = azs.map((az, i) => {
      const u = (az - isl.azDeg) / isl.halfWidthDeg; // -1..1
      const shape = islandShape(u);
      return isl.peakM * shape * (0.88 + 0.12 * hash01(seed, 'isl-rough', n, i));
    });
    if (isl.aligned) heights[Math.floor(samples / 2)] = isl.peakM; // the exact peak the sun sets on
    const dist = isl.aligned ? ALIGNED_ISLAND_DISTANCE_M : isl.distanceM;
    const mesh = silhouette(azs, () => dist, heights, 0x4a5a58);
    mesh.name = `far_island_${n}`;
    group.add(mesh);
  }
  return group;
}

/** Far sector ground, from where the near terrain ends (TERRAIN_RADIUS_M) down the hill to the shore. */
export function buildGround(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'ground';
  const rings = [TERRAIN_RADIUS_M, 400, 520, 660, 820, 1000, 1200, 1400, SHORE_M];
  const azSteps = 60;
  const pos: number[] = [];
  const idx: number[] = [];
  rings.forEach((d, r) => {
    for (let a = 0; a <= azSteps; a++) {
      const az = AZ_FROM + ((AZ_TO - AZ_FROM) * a) / azSteps;
      pos.push(...alongAzimuth(az, d, groundY(d)));
      if (r > 0 && a > 0) {
        const cur = r * (azSteps + 1) + a;
        const prev = cur - (azSteps + 1);
        idx.push(prev - 1, cur - 1, prev, prev, cur - 1, cur);
      }
    }
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  const hill = new THREE.Mesh(g, new THREE.MeshStandardMaterial({ color: 0x3d4a32, roughness: 1, side: THREE.DoubleSide }));
  hill.name = 'ground_hill';
  group.add(hill);
  return group;
}

/** Nothing of the slope's vegetation stands on the terrace (or its wall band), the lane or the stairs. */
function clearOfBuilt(x: number, z: number, r: number): boolean {
  return outlineDistance(x, z) > r + TERRACE_BAND_M && laneWeight(x, z) === 0 && !nearStairs(x, z, r);
}

/** Sugi (Japanese cedar) grove on the slope left of the terrace: instanced tall cones rooted on the ground. */
export function buildSugi(seed: number): THREE.InstancedMesh {
  const count = 60;
  const mesh = new THREE.InstancedMesh(
    new THREE.ConeGeometry(1.6, 1, 7),
    new THREE.MeshStandardMaterial({ color: 0x1f3322, roughness: 1 }),
    count,
  );
  mesh.name = 'sugi';
  const xs = SUGI_GROVE.map((p) => p[0]);
  const zs = SUGI_GROVE.map((p) => p[1]);
  const [x0, x1, z0, z1] = [Math.min(...xs), Math.max(...xs), Math.min(...zs), Math.max(...zs)];
  const m = new THREE.Matrix4();
  let placed = 0;
  for (let k = 0; placed < count && k < count * 50; k++) {
    const x = x0 + (x1 - x0) * hash01(seed, 'sugi-x', k);
    const z = z0 + (z1 - z0) * hash01(seed, 'sugi-z', k);
    if (!insidePolygon([x, z], SUGI_GROVE) || !clearOfBuilt(x, z, 1.6)) continue;
    const h = 16 + 10 * hash01(seed, 'sugi-h', k);
    const base = surfaceY(x, z) - 0.3; // rooted: the cone's base sits just into the ground
    m.compose(new THREE.Vector3(x, base + h / 2, z), new THREE.Quaternion(), new THREE.Vector3(1 + 0.5 * hash01(seed, 'sugi-w', k), h, 1));
    mesh.setMatrixAt(placed++, m);
  }
  mesh.count = placed;
  return mesh;
}

/**
 * Tree clumps on the slope between the terrace and the town: depth cues and a soft screen in front of the houses.
 * Rooted on the built ground; a clump that would stand on the terrace, lane or stairs is left out (each clump's place
 * comes from its own index, so the others do not move).
 */
export function buildHillTrees(seed: number): THREE.InstancedMesh {
  const count = 900;
  const mesh = new THREE.InstancedMesh(
    new THREE.IcosahedronGeometry(1, 1),
    new THREE.MeshStandardMaterial({ color: 0x2c3d24, roughness: 1, flatShading: true }),
    count,
  );
  mesh.name = 'hill_trees';
  const m = new THREE.Matrix4();
  let placed = 0;
  for (let i = 0; i < count; i++) {
    const az = AZ_FROM + (AZ_TO - AZ_FROM) * hash01(seed, 'ht-az', i);
    const d = 14 + 640 * Math.pow(hash01(seed, 'ht-d', i), 1.6);
    const r = (2.5 + 3.5 * hash01(seed, 'ht-r', i)) * (0.6 + d / 700);
    const p = alongAzimuth(az, d, 0);
    if (!clearOfBuilt(p[0], p[2], r)) continue;
    p[1] = (d < TERRAIN_RADIUS_M ? surfaceY(p[0], p[2]) : groundY(d)) + r * 0.7;
    m.compose(new THREE.Vector3(...p), new THREE.Quaternion(), new THREE.Vector3(r, r * 0.8, r));
    mesh.setMatrixAt(placed++, m);
  }
  mesh.count = placed;
  return mesh;
}

export interface Town {
  group: THREE.Group;
  windows: THREE.InstancedMesh;
  /** Mirrored, vertically stretched window copies: light streaks on the water (land occludes them elsewhere). */
  reflections: THREE.InstancedMesh;
  houses: House[];
  craneLights: THREE.Mesh[];
}

const LIT = new THREE.Color(1.6, 1.05, 0.55);
const DARK = new THREE.Color(0.035, 0.035, 0.045);
const BLACK = new THREE.Color(0, 0, 0);

export function buildTown(seed: number): Town {
  const houses = townLayout(seed);
  const group = new THREE.Group();
  group.name = 'town';
  const walls = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshStandardMaterial({ color: 0xb9b2a4, roughness: 0.9 }), houses.length);
  const roofs = new THREE.InstancedMesh(new THREE.ConeGeometry(0.75, 1, 4), new THREE.MeshStandardMaterial({ color: 0x3a4148, roughness: 0.7 }), houses.length);
  const totalWindows = houses.reduce((s, h) => s + windowCount(h), 0);
  const windows = new THREE.InstancedMesh(new THREE.PlaneGeometry(0.9, 0.9), new THREE.MeshBasicMaterial({ color: 0xffffff }), totalWindows);
  walls.name = 'town_walls';
  roofs.name = 'town_roofs';
  windows.name = 'town_windows';

  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  let w = 0;
  houses.forEach((h, i) => {
    const base = alongAzimuth(h.azDeg, h.distanceM, groundY(h.distanceM));
    const yaw = Math.atan2(EYE[0] - base[0], EYE[2] - base[2]);
    q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    const height = h.floors * 3;
    m.compose(new THREE.Vector3(base[0], base[1] + height / 2, base[2]), q, new THREE.Vector3(h.widthM, height, h.depthM));
    walls.setMatrixAt(i, m);
    const rq = q.clone().multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 4));
    m.compose(new THREE.Vector3(base[0], base[1] + height + 1.2, base[2]), rq, new THREE.Vector3(h.widthM * 0.95, 2.4, h.depthM * 0.95));
    roofs.setMatrixAt(i, m);
    const face = new THREE.Vector3(0, 0, h.depthM / 2 + 0.05).applyQuaternion(q);
    const side = new THREE.Vector3(1, 0, 0).applyQuaternion(q);
    for (let f = 0; f < h.floors; f++) {
      for (const s of [-0.25, 0.25]) {
        const p = new THREE.Vector3(base[0], base[1] + f * 3 + 1.6, base[2]).add(face).addScaledVector(side, s * h.widthM);
        m.compose(p, q, new THREE.Vector3(1, 1, 1));
        windows.setMatrixAt(w, m);
        windows.setColorAt(w, DARK);
        w++;
      }
    }
  });

  const craneLights: THREE.Mesh[] = [];
  const steel = new THREE.MeshStandardMaterial({ color: 0x8a8f96, roughness: 0.6 });
  const red = new THREE.MeshBasicMaterial({ color: new THREE.Color(4, 0.5, 0.3) });
  [232, 238, 262].forEach((az, i) => {
    const base = alongAzimuth(az, TOWN.farM + 40, groundY(TOWN.farM + 40));
    const yaw = Math.atan2(EYE[0] - base[0], EYE[2] - base[2]) + Math.PI / 2 + i * 0.4;
    const crane = new THREE.Group();
    crane.name = `crane_${i}`;
    const tower = new THREE.Mesh(new THREE.BoxGeometry(2, 34, 2), steel);
    tower.position.y = 17;
    const jib = new THREE.Mesh(new THREE.BoxGeometry(32, 1.6, 1.6), steel);
    jib.position.set(8, 33, 0);
    const light = new THREE.Mesh(new THREE.SphereGeometry(0.9, 8, 6), red);
    light.position.set(0, 35.5, 0);
    crane.add(tower, jib, light);
    crane.position.set(...base);
    crane.rotation.y = yaw;
    group.add(crane);
    craneLights.push(light);
  });

  const reflections = new THREE.InstancedMesh(
    windows.geometry,
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.35, blending: THREE.AdditiveBlending, depthWrite: false }),
    windows.count,
  );
  reflections.name = 'town_reflections';
  const mp = new THREE.Vector3();
  const mq = new THREE.Quaternion();
  const ms = new THREE.Vector3();
  for (let i = 0; i < windows.count; i++) {
    windows.getMatrixAt(i, m);
    m.decompose(mp, mq, ms);
    mp.y = 2 * SEA_LEVEL_Y - mp.y - 2.5;
    m.compose(mp, mq, new THREE.Vector3(ms.x * 0.8, ms.y * 6, ms.z));
    reflections.setMatrixAt(i, m);
    reflections.setColorAt(i, DARK);
  }
  group.add(walls, roofs, windows, reflections);
  return { group, windows, reflections, houses, craneLights };
}

/** Windows follow their deterministic schedule; crane lights blink 1 Hz (on 0.4 s of each second, animTime). */
export function updateTown(town: Town, state: WorldState, seed: number, animTime: number, compensation = 1): void {
  (town.windows.material as THREE.MeshBasicMaterial).color.setScalar(compensation);
  for (const l of town.craneLights) (l.material as THREE.MeshBasicMaterial).color.setRGB(4 * compensation, 0.5 * compensation, 0.3 * compensation);
  const hour = state.time.local.hour + state.time.local.minute / 60;
  let w = 0;
  for (const h of town.houses) {
    for (let k = 0; k < windowCount(h); k++, w++) {
      const lit = windowLit(seed, h.id * 16 + k, hour, state.sky.sun.altDeg);
      town.windows.setColorAt(w, lit ? LIT : DARK);
      town.reflections.setColorAt(w, lit ? LIT : BLACK);
    }
  }
  if (town.windows.instanceColor) town.windows.instanceColor.needsUpdate = true;
  if (town.reflections.instanceColor) town.reflections.instanceColor.needsUpdate = true;
  (town.reflections.material as THREE.MeshBasicMaterial).color.setScalar(compensation);
  const on = animTime - Math.floor(animTime) < 0.4;
  for (const l of town.craneLights) l.visible = on;
}

/** Sagging wires between consecutive poles' anchors (world positions). */
export function buildWires(anchors: Array<[Vec3, Vec3]>): THREE.Group {
  const group = new THREE.Group();
  group.name = 'wires';
  const material = new THREE.LineBasicMaterial({ color: 0x1a1a1a });
  for (let i = 0; i + 1 < anchors.length; i++) {
    for (const k of [0, 1] as const) {
      const a = anchors[i]![k];
      const b = anchors[i + 1]![k];
      const span = Math.hypot(b[0] - a[0], b[2] - a[2]);
      const pts = catenary(a, b, span * 0.025, 24).map((p) => new THREE.Vector3(...p));
      group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), material));
    }
  }
  return group;
}
